import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronDown, ChevronUp, Lock } from "lucide-react";
import { http } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Panel, Confirm, SortableList, Pill } from "./ui";

const TYPES = [
  { v: "text", l: "Texto corto" }, { v: "textarea", l: "Texto largo" }, { v: "number", l: "Número" },
  { v: "date", l: "Fecha" }, { v: "select", l: "Lista (una opción)" }, { v: "multiselect", l: "Selección múltiple" },
  { v: "colors", l: "Colores (selección múltiple)" }, { v: "file", l: "Adjuntar fotografía" },
];
const HAS_OPTIONS = ["select", "multiselect", "colors"];

function Options({ field, onChange }) {
  const opts = field.options || [];
  const [t, setT] = useState("");
  const [hex, setHex] = useState("#E51C2A");
  const isColor = field.type === "colors";
  const add = () => { if (!t.trim()) return; onChange([...opts, isColor ? { label: t.trim(), hex } : { label: t.trim() }]); setT(""); };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {opts.map((o, i) => (
          <span key={o.label + i} className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-neutral-100 text-sm" data-testid={`option-${field.id}-${i}`}>
            {isColor && <input type="color" value={o.hex || "#ffffff"} onChange={(e) => onChange(opts.map((x, j) => (j === i ? { ...x, hex: e.target.value } : x)))} className="h-4 w-4 rounded-full cursor-pointer" />}
            <input value={o.label} onChange={(e) => onChange(opts.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className="bg-transparent w-24 outline-none" />
            <button type="button" onClick={() => onChange(opts.filter((_, j) => j !== i))} className="h-5 w-5 rounded-full hover:bg-neutral-200 flex items-center justify-center"><Trash2 size={11} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        {isColor && <input type="color" value={hex} onChange={(e) => setHex(e.target.value)} className="h-10 w-10 rounded cursor-pointer" />}
        <Input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={isColor ? "Nuevo color (ej. Lila)" : "Nueva opción"} data-testid={`new-option-${field.id}`} />
        <Button type="button" variant="outline" onClick={add} data-testid={`add-option-${field.id}`}><Plus size={14} /></Button>
      </div>
    </div>
  );
}

function FieldCard({ f, onChange, onDelete }) {
  const [open, setOpen] = useState(false);
  const set = (k, v) => onChange({ ...f, [k]: v });
  return (
    <div className={`bg-white rounded-xl border border-black/5 ${f.active === false ? "opacity-60" : ""}`} data-testid={`form-field-${f.id}`}>
      <div className="flex items-center gap-3 p-3">
        <button type="button" className="flex-1 text-left min-w-0" onClick={() => setOpen(!open)} data-testid={`toggle-field-${f.id}`}>
          <div className="font-semibold truncate">{f.label || "(sin título)"} {f.required && <span className="text-[var(--brand-primary)]">*</span>}</div>
          <div className="flex gap-1 mt-1">
            <Pill className="bg-neutral-100 text-neutral-600">{TYPES.find((t) => t.v === f.type)?.l}</Pill>
            {f.system && <Pill className="bg-amber-50 text-amber-700"><Lock size={9} className="mr-1" /> Necesario</Pill>}
          </div>
        </button>
        <div className="flex items-center gap-2 text-xs"><span className="hidden sm:inline">Activo</span><Switch checked={f.active !== false} disabled={f.system} onCheckedChange={(v) => set("active", v)} data-testid={`active-field-${f.id}`} /></div>
        <button type="button" onClick={() => setOpen(!open)} className="text-neutral-500">{open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</button>
      </div>
      {open && (
        <div className="border-t p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Pregunta</Label><Input value={f.label} onChange={(e) => set("label", e.target.value)} data-testid={`label-field-${f.id}`} /></div>
          <div>
            <Label>Tipo de respuesta</Label>
            <select value={f.type} disabled={f.system} onChange={(e) => set("type", e.target.value)} className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white" data-testid={`type-field-${f.id}`}>
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div><Label>Texto de ayuda (placeholder)</Label><Input value={f.placeholder || ""} onChange={(e) => set("placeholder", e.target.value)} /></div>
          <div>
            <Label>Ancho</Label>
            <select value={f.width || "half"} onChange={(e) => set("width", e.target.value)} className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white">
              <option value="half">Media fila</option><option value="full">Fila completa</option>
            </select>
          </div>
          <div className="flex items-center gap-2"><Switch checked={!!f.required} disabled={f.system} onCheckedChange={(v) => set("required", v)} data-testid={`required-field-${f.id}`} /><span className="text-sm">Obligatoria</span></div>
          {HAS_OPTIONS.includes(f.type) && <div className="sm:col-span-2"><Label>Opciones</Label><Options field={f} onChange={(v) => set("options", v)} /></div>}
          {!f.system && (
            <div className="sm:col-span-2 flex justify-end">
              <Confirm onConfirm={onDelete} title={`¿Eliminar la pregunta "${f.label}"?`} description="Las cotizaciones antiguas conservan sus respuestas."
                trigger={<Button variant="outline" size="sm" className="text-red-600" data-testid={`delete-field-${f.id}`}><Trash2 size={14} className="mr-1" /> Eliminar pregunta</Button>} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FormBuilderPage() {
  const [fields, setFields] = useState([]);
  const [orig, setOrig] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { http.get("/quote-form").then((r) => { setFields(r.data.fields); setOrig(JSON.stringify(r.data.fields)); }); }, []);
  const dirty = useMemo(() => JSON.stringify(fields) !== orig, [fields, orig]);

  const add = (type) => setFields((fs) => [...fs, { id: `campo_${Date.now().toString(36)}`, label: "Nueva pregunta", type, required: false, active: true, width: HAS_OPTIONS.includes(type) || type === "textarea" || type === "file" ? "full" : "half", options: HAS_OPTIONS.includes(type) ? [{ label: "Opción 1" }] : [] }]);
  async function save() {
    setSaving(true);
    try {
      const { data } = await http.put("/quote-form", { fields });
      setOrig(JSON.stringify(data.fields));
      toast.success("Formulario publicado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); } finally { setSaving(false); }
  }

  return (
    <div data-testid="admin-form-builder">
      <PageHeader title="Formulario de cotización" subtitle="Decide qué información pides al cliente. Arrastra para cambiar el orden."
        actions={<>
          <a href="/cotizacion" target="_blank" rel="noreferrer"><Button variant="outline">Ver formulario público</Button></a>
          <Button className="btn-primary" onClick={save} disabled={!dirty || saving} data-testid="form-save"><Save size={14} className="mr-1" /> {dirty ? "Guardar y publicar" : "Publicado"}</Button>
        </>} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SortableList items={fields} onReorder={setFields}
            renderItem={(f, i) => <FieldCard f={f} onChange={(nf) => setFields((fs) => fs.map((x, j) => (j === i ? nf : x)))} onDelete={() => setFields((fs) => fs.filter((_, j) => j !== i))} />} />
        </div>
        <Panel title="Agregar pregunta" className="h-fit lg:sticky lg:top-6">
          <div className="grid grid-cols-1 gap-2">
            {TYPES.map((t) => (
              <Button key={t.v} variant="outline" className="justify-start" onClick={() => add(t.v)} data-testid={`add-field-${t.v}`}><Plus size={14} className="mr-2" /> {t.l}</Button>
            ))}
          </div>
          <p className="text-xs text-neutral-500 mt-4">Nombre y WhatsApp son necesarios para contactar al cliente y no se pueden eliminar.</p>
        </Panel>
      </div>
    </div>
  );
}
